/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';

// ═══════════════════════════════════════════════════════════════
// Page Activité — feed cross-dossiers du workspace
// Visible uniquement pour les rôles internes (admin/formaliste/superviseur/support)
// ═══════════════════════════════════════════════════════════════

const EVENT_ICONS = {
  note_added: '💬',
  dossier_create: '➕',
  dossier_created: '➕',
  status_change: '🔄',
  status_changed: '🔄',
  dossier_update: '✏️',
  dossier_update_admin: '✏️',
  assigned: '👤',
  formaliste_assigned: '👤',
  document_upload: '📎',
  attachment_upload: '📎',
  kbis_received: '✅',
  transmitted_gtc: '📨',
  airtable_link: '🔗',
  airtable_import: '⬇️',
  airtable_import_create: '⬇️',
  aircall_manual_link: '📞',
  zendesk_manual_link: '🎫',
};

const EVENT_COLORS = {
  note_added: '#B8860B',
  dossier_create: '#16a34a',
  dossier_created: '#16a34a',
  status_change: '#3b82f6',
  status_changed: '#3b82f6',
  assigned: '#8b5cf6',
  formaliste_assigned: '#8b5cf6',
  document_upload: '#0ea5e9',
  attachment_upload: '#0ea5e9',
  kbis_received: '#16a34a',
  transmitted_gtc: '#16a34a',
};

function colorOfActor(name) {
  const palette = ['#B8860B', '#8b5cf6', '#3b82f6', '#16a34a', '#dc2626', '#f59e0b', '#0ea5e9', '#a855f7'];
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return palette[hash % palette.length];
}

function initialsOf(name) {
  if (!name) return '?';
  return name.trim().split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'à l\'instant';
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' });
}

function groupByDay(items) {
  const groups = {};
  items.forEach(item => {
    if (!item.created_at) return;
    const d = new Date(item.created_at);
    const key = d.toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });
  return Object.entries(groups);
}

export default function Activite() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [actorFilter, setActorFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem('lexback_token');
    fetch(`${API_URL}/api/activity/feed?hours=${hours}&limit=200`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [hours]);

  // Acteurs distincts pour le filtre
  const actors = useMemo(() => {
    const set = new Set();
    items.forEach(it => { if (it.actor_name) set.add(it.actor_name); });
    return Array.from(set).sort();
  }, [items]);

  // Types distincts
  const eventTypes = useMemo(() => {
    const set = new Set();
    items.forEach(it => { if (it.event_type) set.add(it.event_type); });
    return Array.from(set).sort();
  }, [items]);

  // Filtres appliqués
  const filtered = useMemo(() => {
    let list = items;
    if (actorFilter) list = list.filter(it => it.actor_name === actorFilter);
    if (typeFilter) list = list.filter(it => it.event_type === typeFilter);
    return list;
  }, [items, actorFilter, typeFilter]);

  const groups = useMemo(() => groupByDay(filtered), [filtered]);

  const ranges = [
    { val: 1, label: '1 h' },
    { val: 24, label: '24 h' },
    { val: 72, label: '3 j' },
    { val: 168, label: '7 j' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
        <div>
          <div className="section-title">Activité récente</div>
          <div style={{ fontSize:'13px', color:'var(--color-text-secondary)', marginTop:'2px' }}>
            Tout ce qui s'est passé sur la plateforme
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'1.5rem', flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:'4px', padding:'3px', background:'var(--color-background-secondary)', borderRadius:'8px', border:'0.5px solid var(--color-border-tertiary)' }}>
          {ranges.map(r => (
            <button key={r.val} onClick={() => setHours(r.val)} style={{
              padding:'5px 14px', borderRadius:'6px', fontSize:'12px', cursor:'pointer',
              border:'none',
              background: hours === r.val ? 'var(--color-background-primary)' : 'transparent',
              color: hours === r.val ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              fontWeight: hours === r.val ? 500 : 400,
              boxShadow: hours === r.val ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}>{r.label}</button>
          ))}
        </div>
        <div style={{ flex:1 }} />
        {actors.length > 0 && (
          <select value={actorFilter} onChange={e => setActorFilter(e.target.value)} style={{
            padding:'6px 10px', borderRadius:'8px',
            border:'0.5px solid var(--color-border-secondary)',
            fontSize:'12px', background:'var(--color-background-primary)',
            color:'var(--color-text-primary)', cursor:'pointer',
          }}>
            <option value="">Tous les utilisateurs</option>
            {actors.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        {eventTypes.length > 0 && (
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{
            padding:'6px 10px', borderRadius:'8px',
            border:'0.5px solid var(--color-border-secondary)',
            fontSize:'12px', background:'var(--color-background-primary)',
            color:'var(--color-text-primary)', cursor:'pointer',
          }}>
            <option value="">Tous les événements</option>
            {eventTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        )}
      </div>

      {/* Counter */}
      <div style={{ fontSize:'11px', color:'var(--color-text-secondary)', marginBottom:'12px' }}>
        {!loading && (
          <>{filtered.length} action{filtered.length > 1 ? 's' : ''} {filtered.length !== items.length && <span style={{ color:'var(--color-text-tertiary)' }}>sur {items.length}</span>}</>
        )}
      </div>

      {/* Feed */}
      {loading ? (
        <div style={{ padding:'2rem', textAlign:'center', color:'var(--color-text-secondary)' }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding:'2rem', textAlign:'center', color:'var(--color-text-secondary)' }}>
          Aucune activité sur cette période.
        </div>
      ) : (
        <div style={{
          background:'var(--color-background-primary)',
          border:'0.5px solid var(--color-border-secondary)',
          borderRadius:'12px',
          padding:'16px 20px',
          boxShadow:'0 1px 3px rgba(0,0,0,0.04)',
        }}>
          {groups.map(([day, dayItems], gi) => (
            <div key={day} style={{ marginBottom: gi === groups.length - 1 ? 0 : '20px' }}>
              <div style={{
                fontSize:'10.5px', fontWeight:600, textTransform:'uppercase',
                color:'#9a8f7a', letterSpacing:'0.06em',
                marginBottom:'10px', paddingBottom:'4px',
                borderBottom:'1px solid #ece6da',
              }}>
                {day}
              </div>
              <div style={{ display:'flex', flexDirection:'column' }}>
                {dayItems.map((item, idx) => {
                  const icon = EVENT_ICONS[item.event_type] || '•';
                  const accent = EVENT_COLORS[item.event_type] || '#9a8f7a';
                  return (
                    <div key={item.id} style={{
                      display:'flex', gap:'12px',
                      padding:'10px 0',
                      borderBottom: idx === dayItems.length - 1 ? 'none' : '0.5px solid #f0ebe0',
                      cursor: item.dossier_id ? 'pointer' : 'default',
                      transition:'background 0.15s',
                    }}
                      onClick={() => item.dossier_id && navigate(`/dossiers?open=${item.dossier_id}`)}
                      onMouseEnter={e => { if (item.dossier_id) e.currentTarget.style.background = '#faf7f1'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width:'32px', height:'32px', borderRadius:'50%',
                        background: colorOfActor(item.actor_name),
                        color:'white', fontSize:'11px', fontWeight:600,
                        display:'flex', alignItems:'center', justifyContent:'center',
                        flexShrink:0, position:'relative',
                      }}>
                        {initialsOf(item.actor_name)}
                        <span style={{
                          position:'absolute', bottom:'-3px', right:'-3px',
                          width:'16px', height:'16px', borderRadius:'50%',
                          background:'#fff', border:`1.5px solid ${accent}`,
                          fontSize:'9px', display:'flex', alignItems:'center', justifyContent:'center',
                        }}>{icon}</span>
                      </div>

                      {/* Content */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'13px', color:'#1a1a1a', lineHeight:1.4 }}>
                          <strong style={{ fontWeight:600 }}>{item.actor_name || 'Système'}</strong>
                          {' '}
                          <span style={{ color:'#5c5446' }}>
                            {(item.summary || '').replace(item.actor_name || '', '').trim() || (item.event_type || '').replace(/_/g, ' ')}
                          </span>
                        </div>
                        {item.dossier_societe && (
                          <div style={{ fontSize:'11.5px', color:'#9a8f7a', marginTop:'2px' }}>
                            <span style={{ fontFamily:'JetBrains Mono, monospace', color:'#B8860B' }}>{item.dossier_reference}</span>
                            {' · '}
                            <span>{item.dossier_societe}</span>
                          </div>
                        )}
                        {item.content_excerpt && (
                          <div style={{
                            fontSize:'12px', color:'#5c5446', marginTop:'5px',
                            padding:'6px 10px', background:'#f5f1ea', borderRadius:'6px',
                            borderLeft:`3px solid ${accent}`,
                            fontStyle:'italic',
                          }}>
                            « {item.content_excerpt}{item.content_excerpt.length >= 200 ? '…' : ''} »
                          </div>
                        )}
                      </div>

                      {/* Time */}
                      <div style={{ fontSize:'10.5px', color:'#9a8f7a', whiteSpace:'nowrap', flexShrink:0, paddingTop:'4px' }}>
                        {formatRelative(item.created_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
